import React, { useState } from 'react';
import type { AdminNav, AdminNavPermissions } from './adminShared';
import { isAdminNavAllowed } from './adminShared';
import { useAdminDevMode } from './adminDevMode';

export const ADMIN_NAV_ITEMS: { id: AdminNav; label: string; icon: string }[] = [
  { id: 'overview', label: 'Обзор', icon: 'fa-chart-pie' },
  { id: 'industries', label: 'Индустрии', icon: 'fa-layer-group' },
  { id: 'sectors', label: 'Сектора', icon: 'fa-sitemap' },
  { id: 'companies', label: 'Компании', icon: 'fa-building' },
  { id: 'records', label: 'Записи сотрудников', icon: 'fa-users' },
  { id: 'record-grading', label: 'Грейдирование', icon: 'fa-star-half-stroke' },
  { id: 'grading', label: 'Грейдинг', icon: 'fa-diagram-project' },
  { id: 'community', label: 'Сообщество', icon: 'fa-user-group' },
  { id: 'permissions', label: 'Права доступа', icon: 'fa-user-shield' },
  { id: 'subscriptions', label: 'Подписки', icon: 'fa-id-card' },
];

type AdminNavDropdownProps = {
  activeNav: AdminNav;
  onNav: (nav: AdminNav) => void;
  isSectionActive?: boolean;
  navPermissions: AdminNavPermissions;
};

export function AdminNavDropdown({
  activeNav,
  onNav,
  isSectionActive = false,
  navPermissions,
}: AdminNavDropdownProps) {
  const [open, setOpen] = useState(false);
  const triggerLabel = 'Админ-панель';
  const navItems = ADMIN_NAV_ITEMS.filter((item) => isAdminNavAllowed(item.id, navPermissions));

  const subMenu = open ? (
    <div className="dc-admin-nav-submenu dc-admin-nav-submenu--workspace" role="menu">
      {navItems.map((item) => (
        <button
          key={item.id}
          type="button"
          role="menuitem"
          className={`dc-admin-nav-dropdown-item ${activeNav === item.id ? 'dc-admin-nav-dropdown-item-active' : ''}`}
          onClick={() => {
            onNav(item.id);
            setOpen(false);
          }}
        >
          <i className={`fas ${item.icon}`} aria-hidden="true" />
          <span>{item.label}</span>
        </button>
      ))}
    </div>
  ) : null;

  return (
    <li className={`nav-item nav-item--dropdown ${isSectionActive ? 'active' : ''} ${open ? 'nav-item--dropdown-open' : ''}`}>
      <button
        type="button"
        className="nav-link"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((value) => !value)}
      >
        <i className="fas fa-shield-alt" aria-hidden="true" />
        <span className="flex-1">{triggerLabel}</span>
        <i className={`fas fa-chevron-${open ? 'up' : 'down'} text-xs opacity-70`} aria-hidden="true" />
      </button>
      {subMenu}
    </li>
  );
}

export function AdminLayout({
  pageTitle,
  children,
  headerActions,
}: {
  pageTitle: string;
  children: React.ReactNode;
  headerActions?: React.ReactNode;
}) {
  return (
    <div className="dc-admin-shell">
      <div className="dc-admin-content-col">
        <header className="dc-admin-topbar">
          <h1 className="shrink-0 text-base font-semibold text-slate-900">{pageTitle}</h1>
          <div className="ml-auto flex shrink-0 items-center gap-2">
            {headerActions}
            <AdminDevToggle />
            <button type="button" className="dc-btn-secondary h-9 w-9 p-0" aria-label="Уведомления">
              <i className="far fa-bell" aria-hidden="true" />
            </button>
            <button type="button" className="dc-btn-secondary h-9 w-9 p-0" aria-label="Справка">
              <i className="far fa-question-circle" aria-hidden="true" />
            </button>
            <button type="button" className="dc-btn-secondary ml-1 gap-2 py-1.5 pl-1.5 pr-2">
              <span className="flex h-8 w-8 items-center justify-center rounded-md bg-dc-primary text-xs font-semibold text-white">AD</span>
              <span>Админ</span>
              <i className="fas fa-chevron-down text-[10px] text-slate-400" aria-hidden="true" />
            </button>
          </div>
        </header>

        <main className="dc-admin-main">{children}</main>
      </div>
    </div>
  );
}

export function AdminBreadcrumbsTw({ items }: { items: { label: string; onClick?: () => void }[] }) {
  return (
    <nav className="mb-4 flex flex-wrap items-center gap-1 text-sm text-slate-500" aria-label="Навигация">
      {items.map((item, index) => (
        <React.Fragment key={`${item.label}-${index}`}>
          {index > 0 && <span className="text-slate-300">/</span>}
          {item.onClick ? (
            <button type="button" onClick={item.onClick} className="dc-btn-link">{item.label}</button>
          ) : (
            <span className="text-slate-700">{item.label}</span>
          )}
        </React.Fragment>
      ))}
    </nav>
  );
}

export function AdminCard({ children, className = '', padding = true }: { children: React.ReactNode; className?: string; padding?: boolean }) {
  return <div className={`dc-card ${padding ? '' : 'p-0'} ${className}`}>{children}</div>;
}

export function AdminPageHeader({ title, subtitle, actions }: { title: string; subtitle?: string; actions?: React.ReactNode }) {
  return (
    <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
      <div>
        <h2 className="text-2xl font-bold text-slate-900">{title}</h2>
        {subtitle && <p className="mt-1 text-sm text-dc-muted">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function AdminStatCard({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="dc-stat-card">
      <div className="text-xs text-dc-muted">{label}</div>
      <div className="mt-1 text-lg font-semibold text-slate-900">{value}</div>
    </div>
  );
}

export function AdminBadge({ children, variant = 'blue' }: { children: React.ReactNode; variant?: 'blue' | 'green' }) {
  return <span className={variant === 'green' ? 'dc-badge-green' : 'dc-badge-blue'}>{children}</span>;
}

export const btnPrimary = 'dc-btn-primary';
export const btnSecondary = 'dc-btn-secondary';
export const btnDanger = 'dc-btn-danger';
export const btnGhost = 'dc-btn-secondary';
export const btnLink = 'dc-btn-link';
export const inputClass = 'dc-input';
export const selectClass = 'dc-select';
export const textareaClass = 'dc-textarea';
export const thClass = 'dc-th';
export const tdClass = 'dc-td';
export const filtersRowClass = 'dc-filters-row';
export const filterSearchWrapClass = 'dc-filter-search-wrap';
export const selectFullClass = 'dc-select-full';

export function AdminDevToggle() {
  const { devMode, toggleDevMode } = useAdminDevMode();
  return (
    <button
      type="button"
      className={`dc-dev-toggle${devMode ? ' dc-dev-toggle-on' : ''}`}
      onClick={toggleDevMode}
      title={devMode ? 'Скрыть имена полей БД' : 'Показать имена полей БД'}
      aria-pressed={devMode}
    >
      <i className="fas fa-code text-xs" aria-hidden="true" />
      <span>{devMode ? 'Dev ON' : 'Dev'}</span>
    </button>
  );
}

export function AdminDevTh({
  label,
  dbField,
  className = '',
}: {
  label: string;
  dbField?: string;
  className?: string;
}) {
  const { devMode } = useAdminDevMode();
  return (
    <th className={`${thClass} ${className}`.trim()}>
      {devMode && dbField ? <div className="dc-dev-field-name">{dbField}</div> : null}
      {label}
    </th>
  );
}

export function AdminDevRowMeta({ parts }: { parts: (string | false | null | undefined)[] }) {
  const { devMode } = useAdminDevMode();
  const line = parts.filter(Boolean).join(' · ');
  if (!devMode || !line) return null;
  return <div className="dc-dev-field-name mt-0.5">{line}</div>;
}

export function AdminVisibilityField({
  checked,
  onChange,
  dbField = 'is_visible',
  className = '',
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  dbField?: string;
  className?: string;
}) {
  return (
    <AdminFieldTw
      label="Видно пользователям сообщества"
      dbField={dbField}
      hint="Скрытые записи не попадают в анкету грейдинга и справочники для участников сообщества."
      className={className}
    >
      <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-slate-700">
        <input
          type="checkbox"
          className="h-4 w-4 rounded border-slate-300 text-dc-accent focus:ring-dc-accent"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
        />
        {checked ? 'Да, видно' : 'Нет, скрыто'}
      </label>
    </AdminFieldTw>
  );
}

export function AdminFieldTw({
  label,
  dbField,
  required,
  error,
  children,
  className = '',
  hint,
}: {
  label: string;
  dbField?: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
  className?: string;
  hint?: string;
}) {
  const { devMode } = useAdminDevMode();
  return (
    <div className={className}>
      <label className="mb-1.5 block text-sm font-medium text-slate-700">
        {label}
        {devMode && dbField ? <span className="dc-dev-field-name-inline">{dbField}</span> : null}
        {required && <span className="ml-0.5 text-red-500">*</span>}
      </label>
      {children}
      {hint && !error && <p className="mt-1 text-xs text-dc-muted">{hint}</p>}
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}

export function AdminTableWrap({ children, refreshing, className = '' }: { children: React.ReactNode; refreshing?: boolean; className?: string }) {
  return (
    <div className={`dc-table-wrap ${refreshing ? 'dc-table-wrap-refreshing' : ''} ${className}`}>
      {refreshing && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/50 text-sm text-slate-600">
          Обновление…
        </div>
      )}
      {children}
    </div>
  );
}

export function AdminEmpty({ children }: { children: React.ReactNode }) {
  return <div className="flex items-center justify-center py-16 text-sm text-dc-muted">{children}</div>;
}

export function AdminModalTw({
  title,
  children,
  onClose,
  footer,
  wide,
  size,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
  footer?: React.ReactNode;
  /** @deprecated используйте size="wide" */
  wide?: boolean;
  size?: 'default' | 'wide' | 'xl';
}) {
  const resolvedSize = size ?? (wide ? 'wide' : 'default');
  const sizeClass = resolvedSize === 'xl'
    ? 'w-full max-w-[min(96vw,1280px)]'
    : resolvedSize === 'wide'
      ? 'w-full max-w-2xl'
      : 'w-full max-w-lg';

  return (
    <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-slate-900/40 p-4" onClick={onClose} role="presentation">
      <div
        className={`max-h-[90vh] overflow-y-auto rounded-xl border border-slate-200 bg-white p-6 shadow-xl ${sizeClass}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="mb-4 flex items-start justify-between gap-4">
          <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
          <button type="button" onClick={onClose} className="dc-btn-secondary h-8 w-8 p-0">
            <i className="fas fa-times" aria-hidden="true" />
          </button>
        </div>
        {children}
        {footer && <div className="mt-6 flex justify-end gap-2">{footer}</div>}
      </div>
    </div>
  );
}

export function CompanyAvatar({ name }: { name: string }) {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || '')
    .join('');
  return (
    <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-dc-primary text-lg font-bold text-white">
      {initials || '?'}
    </div>
  );
}

export function AdminSectionTitle({ number, title }: { number: number; title: string }) {
  return (
    <h3 className="mb-3 text-sm font-semibold text-slate-900">
      {number}. {title}
    </h3>
  );
}

export type ConfidenceLevel = 'low' | 'moderate' | 'high' | 'excellent';

export function ConfidenceToggle({
  value,
  onChange,
  disabled = false,
}: {
  value: ConfidenceLevel;
  onChange: (v: ConfidenceLevel) => void;
  disabled?: boolean;
}) {
  const options: { id: ConfidenceLevel; label: string }[] = [
    { id: 'low', label: 'Низкая' },
    { id: 'moderate', label: 'Умеренная' },
    { id: 'high', label: 'Высокая' },
    { id: 'excellent', label: 'Очень высокая' },
  ];
  return (
    <div className={`flex rounded-lg border border-slate-200 p-0.5${disabled ? ' bg-slate-50 opacity-90' : ''}`}>
      {options.map((opt) => (
        <button
          key={opt.id}
          type="button"
          disabled={disabled}
          onClick={() => !disabled && onChange(opt.id)}
          className={`flex-1 rounded-md px-2 py-1.5 text-[11px] font-medium leading-tight transition-colors ${
            value === opt.id ? 'bg-dc-primary text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50'
          }${disabled ? ' cursor-default hover:bg-transparent' : ''}`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

export const PAGE_TITLES: Record<AdminNav, string> = {
  overview: 'Обзор',
  industries: 'Индустрии',
  sectors: 'Сектора',
  companies: 'Компании',
  records: 'Записи сотрудников',
  'record-grading': 'Грейдирование',
  grading: 'Грейдинг',
  community: 'Сообщество',
  permissions: 'Права доступа',
  subscriptions: 'Подписки',
};
